import { graphHooks } from '@tunasong/graph-lib/react'
import { Poll as PollIcon } from '@tunasong/icons'
import { useCurrentUser } from '@tunasong/redux'
import type { ManyOfManyPollResponse } from '@tunasong/schemas'
import { isManyOfManyPoll } from '@tunasong/schemas'
import { FormControl, FormGroup, Typography } from '@tunasong/ui-lib'
import type { FC } from 'react'
import invariant from 'tiny-invariant'
import { PlayAllButton, PollAudioProvider } from '../option-audio.js'
import ratingStyles from '../option-rating.module.css'
import type { PollProps } from '../poll.js'
import styles from '../poll.module.css'
import { getProgress, isCompleteResponse } from '../progress-util.js'
import Progress from '../progress.js'
import { sortByRatings, sortPollOptionsIndices } from '../sort.js'
import { useReorderAnimation } from '../use-reorder-animation.js'
import { VoteOptionRow } from '../vote-option-row.js'
import { useManyOfManyResponse } from './use-many-of-many-response.js'

/**
 * Answers save as they are picked and stay editable until the poll closes — there is no Save button. Whether an
 * answer is complete is derived (`isCompleteResponse`), never stored.
 */
export const ManyOfManyPollVote: FC<PollProps> = props => {
  const { poll, userId, onChange, sort } = props

  invariant(isManyOfManyPoll(poll), `Poll must be a ManyOfManyPoll`)

  const { userId: currentUserId } = useCurrentUser()
  const { profile: respondent } = graphHooks.useProfile(userId)
  const response = useManyOfManyResponse(poll, userId)
  const { allResponses, hasLoadedChildren, isExternalRespondent, choices, ratings, comments } = response

  const { name = 'Untitled', finalOption, closed } = poll

  const baseOrder = sort
    ? // Seeded per viewer and poll, so the order holds across re-renders and votes. GH #1968
      sortPollOptionsIndices(poll, allResponses, 'random', `${currentUserId ?? userId}:${poll.id}`)
    : poll.pollOptions.map((_, idx) => idx)

  /** Judged on what this voter sees (their picks this session), so the status follows every pick at once. */
  const shownResponse: ManyOfManyPollResponse = { type: 'pollresponse', pollType: 'many-of-many', choices }
  const isComplete = isCompleteResponse(shownResponse, poll)

  const { responseQuota = Number.MAX_SAFE_INTEGER } = poll
  const numUserResponses = Object.values(choices).filter(value => value === true).length
  const hasReachedQuota = numUserResponses >= responseQuota

  /**
   * Closing the poll is the lock: answers stay editable while it is open, so `allowChange` no longer applies to
   * many-of-many. Picking writes, so another respondent's answers are always read-only.
   */
  const readOnly = Boolean(!onChange || poll.closed || !hasLoadedChildren || isExternalRespondent)

  /** The working order (votes, then thumbs) and the thumbs themselves exist only while this voter can vote. */
  const pollOptionsIndices = readOnly ? baseOrder : sortByRatings(baseOrder, ratings, choices)
  const optionsRef = useReorderAnimation<HTMLDivElement>(pollOptionsIndices)

  const handleVote = (idx: number) => (value: boolean) => response.setChoices({ ...choices, [idx]: value })

  return (
    <PollAudioProvider pollId={poll.id}>
      <div className={styles.container}>
        <Typography variant="subtitle1" className={styles.title}>
          <PollIcon /> {name}
        </Typography>
        <PlayAllButton options={poll.pollOptions} />
        {poll.closed && (
          <Typography variant="caption" color="secondary">
            The Poll is closed.
          </Typography>
        )}
        {!isExternalRespondent && !poll.closed && !isComplete && responseQuota < Number.MAX_SAFE_INTEGER && (
          <Typography variant="caption" display="block">
            You have picked {numUserResponses} of {responseQuota}.
          </Typography>
        )}
        {isComplete ? (
          <Typography variant="caption" color="primary" display="block">
            {isExternalRespondent ? (respondent?.name ?? respondent?.email + ' has') : 'You have'} completed this poll.
          </Typography>
        ) : null}
        <FormControl className={styles.formControl}>
          <FormGroup>
            <div ref={optionsRef} className={ratingStyles.rows}>
              {hasLoadedChildren &&
                pollOptionsIndices.map(idx => (
                  <VoteOptionRow
                    key={idx}
                    idx={idx}
                    option={poll.pollOptions[idx]}
                    picked={choices[idx] ?? false}
                    readOnly={readOnly}
                    voteDisabled={readOnly || (hasReachedQuota && !choices[idx])}
                    showComment={!isExternalRespondent}
                    rating={ratings[idx]}
                    comment={comments[idx]}
                    result={
                      poll.closed ? (
                        <Progress value={getProgress(poll, allResponses, idx)} selected={finalOption?.[idx]} />
                      ) : null
                    }
                    onVote={handleVote(idx)}
                    onRate={rating => response.rate(idx, rating)}
                    onComment={text => response.comment(idx, text)}
                  />
                ))}
            </div>
          </FormGroup>
          {!readOnly && !closed ? (
            <Typography variant="caption" color="textSecondary">
              Your choices are saved as you pick them.
            </Typography>
          ) : null}
        </FormControl>
      </div>
    </PollAudioProvider>
  )
}
